import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ticketAPI } from '../../services/api';
import io from 'socket.io-client';
import { ArrowLeft, Send, MessageCircle, Image, X } from 'lucide-react';
import './TicketChat.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

function TicketChat() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [typing, setTyping] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);
  const [notification, setNotification] = useState(null);
  const [ticketStatus, setTicketStatus] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-scroll whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadTicketStatus = async () => {
    try {
      // Use lightweight status endpoint (much faster - only fetches status field!)
      const response = await ticketAPI.getTicketStatus(ticketId);
      if (response.success && response.status) {
        setTicketStatus(response.status);
      }
    } catch (error) {
      console.error('Error loading ticket status:', error);
      // Fallback: try from messages API
      try {
        const messagesResponse = await ticketAPI.getMessages(ticketId);
        if (messagesResponse.success && messagesResponse.ticketStatus) {
          setTicketStatus(messagesResponse.ticketStatus);
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  };

  const loadMessages = async () => {
    try {
      const response = await ticketAPI.getMessages(ticketId);
      if (response.success) {
        setMessages(response.messages);
        // Set ticket status from messages API response if available (backup)
        if (response.ticketStatus) {
          setTicketStatus(response.ticketStatus);
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  useEffect(() => {
    // Load ticket status IMMEDIATELY (before everything else)
    loadTicketStatus();
    
    // Initialize socket connection
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    
    setSocket(newSocket);
    
    // Log connection events
    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
    });
    
    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
    });
    
    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    // Join the system first
    newSocket.emit('join_system', {
      userId: user.id,
      username: user.username,
      isAdmin: user.is_admin
    });

    // Join the ticket room
    newSocket.emit('join_ticket', {
      ticketId,
      userId: user.id,
      username: user.username,
      isAdmin: user.is_admin
    });
    
    console.log('Joined ticket room:', ticketId);

    // Load initial messages (in parallel with status)
    loadMessages();

    // Listen for new messages
    newSocket.on('new_message', (message) => {
      console.log('New message received:', message);
      setMessages(prev => [...prev, message]);
    });

    // Listen for typing indicator
    newSocket.on('user_typing', ({ username }) => {
      setTyping(username);
    });

    newSocket.on('user_stop_typing', () => {
      setTyping(false);
    });

    // Listen for ticket status changes
    newSocket.on('ticket_status_changed', (data) => {
      // Compare ticket IDs (handle both string and number)
      const currentTicketId = parseInt(ticketId);
      const eventTicketId = parseInt(data.ticketId);
      
      console.log('Status change event received:', { currentTicketId, eventTicketId, data });
      
      if (eventTicketId === currentTicketId) {
        console.log('Ticket status changed in chat:', data);
        setTicketStatus(data.newStatus);
        
        // Clear input if ticket is closed
        if (data.newStatus === 'closed') {
          setNewMessage('');
          setSelectedImage(null);
          setImagePreview(null);
          
          // Stop typing
          if (newSocket) {
            newSocket.emit('stop_typing', { ticketId });
          }
          
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
          }
          
          showNotification('⚠️ Bu talep yönetici tarafından kapatıldı. Artık mesaj gönderemezsiniz.');
        } else {
          showNotification(`✅ Talep durumu güncellendi: ${getStatusText(data.newStatus)}`);
        }
      }
    });

    // Cleanup on unmount
    return () => {
      // Remove all event listeners
      newSocket.off('new_message');
      newSocket.off('user_typing');
      newSocket.off('user_stop_typing');
      newSocket.off('ticket_status_changed');
      newSocket.disconnect();
    };
  }, [ticketId, user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (ticketStatus === 'closed') {
      showNotification('❌ Bu talep kapatılmış. Kapalı taleplere mesaj gönderemezsiniz.');
      return;
    }
    
    if (!newMessage.trim() && !selectedImage) return;
    if (!socket) return;

    let imageUrl = null;

    // Upload image if selected
    if (selectedImage) {
      setUploadingImage(true);
      try {
        const formData = new FormData();
        formData.append('image', selectedImage);
        formData.append('ticket_id', ticketId);

        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
        const response = await fetch(`${API_BASE}/api/upload_image.php`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        const data = await response.json();
        
        if (data.success) {
          imageUrl = data.image_url;
        } else {
          showNotification('❌ Resim yüklenemedi: ' + (data.error || 'Bilinmeyen hata'));
          setUploadingImage(false);
          return;
        }
      } catch (error) {
        console.error('Image upload error:', error);
        showNotification('❌ Resim yüklenirken hata oluştu');
        setUploadingImage(false);
        return;
      }
      setUploadingImage(false);
    }

    // Emit message via socket
    socket.emit('send_message', {
      ticketId,
      userId: user.id,
      username: user.username,
      text: newMessage,
      image_url: imageUrl,
      sender: user.is_admin ? 'admin' : 'user'
    });

    setNewMessage('');
    setSelectedImage(null);
    setImagePreview(null);
    
    // Stop typing indicator
    socket.emit('stop_typing', { ticketId });
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);

    if (!socket) return;

    // Emit typing indicator
    socket.emit('typing', {
      ticketId,
      username: user.username
    });

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('stop_typing', { ticketId });
    }, 2000);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const playNotificationSound = () => {
    try {
      // Handle browser autoplay policy - resume context if suspended
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // Silently fail - sound is optional
      console.log('Sound notification failed:', error);
    }
  };

  const showNotification = (message) => {
    playNotificationSound();
    setNotification(message);
    setTimeout(() => setNotification(null), 5000);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('❌ Lütfen sadece resim dosyası seçin');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('❌ Resim boyutu 5MB\'den küçük olmalıdır');
      return;
    }

    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Beklemede',
      'open': 'Açık',
      'in_progress': 'İşlemde',
      'closed': 'Kapalı'
    };
    return statusMap[status] || status;
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === ticketStatus || !user.is_admin || !ticketStatus) return;
    
    setUpdatingStatus(true);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/update_status.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ticket_id: ticketId,
          status: newStatus
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        const statusText = getStatusText(newStatus);
        let message = `✅ Talep durumu güncellendi: ${statusText}`;
        if (data.sms_sent) {
          message += ' (SMS gönderildi ✓)';
        }
        if (data.notification_sent) {
          message += ' (Kullanıcı bilgilendirildi ✓)';
        }
        showNotification(message);
        // Update status immediately (WebSocket will also update it, but this ensures UI is responsive)
        setTicketStatus(newStatus);
      } else {
        showNotification(`❌ Durum güncellenemedi: ${data.error || 'Bilinmeyen hata'}`);
        // Revert dropdown to previous value
        e.target.value = ticketStatus;
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('❌ Talep durumu güncellenirken hata oluştu');
      // Revert dropdown to previous value
      e.target.value = ticketStatus;
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendSMS = async () => {
    if (!user.is_admin) return;
    
    setSendingSMS(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/send_sms_manual.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ ticket_id: ticketId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('✅ SMS başarıyla gönderildi: ' + data.phone);
      } else {
        showNotification('❌ SMS gönderilemedi: ' + (data.error || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('SMS error:', error);
      showNotification('❌ SMS gönderilirken hata oluştu');
    } finally {
      setSendingSMS(false);
    }
  };

  return (
    <div className="chat-container">
      {/* Notification Banner */}
      {notification && (
        <div className="notification-banner" style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '500',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <span>{notification}</span>
        </div>
      )}
      
      {/* Header */}
      <div className="chat-header">
        <button className="back-btn" onClick={() => navigate(user.is_admin ? '/admin' : '/dashboard')}>
          <ArrowLeft size={20} />
        </button>
        <div className="chat-header-info">
          <h2>Talep #{ticketId}</h2>
          <p className="chat-subtitle">Destek Sohbeti</p>
        </div>
        {user.is_admin && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginLeft: 'auto'
          }}>
            {/* Status Update Dropdown */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <label style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--text-secondary, #6b7280)',
                whiteSpace: 'nowrap'
              }}>
                Durum:
              </label>
              <select
                value={ticketStatus || 'pending'}
                onChange={handleStatusChange}
                disabled={updatingStatus || !ticketStatus}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'var(--bg-primary, #ffffff)',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '6px',
                  color: 'var(--text-primary, #111827)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: updatingStatus ? 'not-allowed' : 'pointer',
                  opacity: updatingStatus ? 0.6 : 1,
                  outline: 'none',
                  transition: 'all 0.2s',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  if (!updatingStatus) {
                    e.target.style.borderColor = 'var(--accent-primary, #3b82f6)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = 'var(--border, #e5e7eb)';
                }}
              >
                <option value="pending">Beklemede</option>
                <option value="open">Açık</option>
                <option value="closed">Kapalı</option>
              </select>
              {updatingStatus && (
                <span style={{
                  fontSize: '11px',
                  color: 'var(--accent-primary, #3b82f6)',
                  fontWeight: '600',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}>
                  Güncelleniyor...
                </span>
              )}
            </div>
            
            {/* SMS Button */}
            <button 
              className="btn-sms" 
              onClick={handleSendSMS}
              disabled={sendingSMS}
              style={{
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: sendingSMS ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '500',
                opacity: sendingSMS ? 0.6 : 1
              }}
            >
              <MessageCircle size={16} />
              {sendingSMS ? 'Gönderiliyor...' : 'SMS Gönder'}
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-chat">
            <p>Henüz mesaj yok. Sohbeti başlatın!</p>
          </div>
        ) : (
          messages.map((message, index) => {
            // Check if message is from current user
            const isMyMessage = user.is_admin 
              ? message.sender === 'admin' 
              : message.sender === 'user';
            
            return (
              <div
                key={message.id || index}
                className={`message ${isMyMessage ? 'message-right' : 'message-left'}`}
              >
                <div className="message-bubble">
                  <div className="message-header">
                    <span className="message-sender">
                      {isMyMessage ? 'Siz' : (message.username || message.sender)}
                    </span>
                  </div>
                  {message.image_url && (
                    <div className="message-image">
                      <img 
                        src={`${process.env.REACT_APP_API_BASE || 'http://localhost:8000'}${message.image_url}`} 
                        alt="Uploaded" 
                        onClick={() => window.open(`${process.env.REACT_APP_API_BASE || 'http://localhost:8000'}${message.image_url}`, '_blank')}
                      />
                    </div>
                  )}
                  {message.text && <div className="message-text">{message.text}</div>}
                  <div className="message-time">
                    {formatTime(message.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        
        {typing && (
          <div className="typing-indicator">
            <span>{typing} yazıyor...</span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Closed Ticket Banner */}
      {ticketStatus === 'closed' && (
        <div style={{
          padding: '12px 24px',
          textAlign: 'center',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span>🔒</span>
          <span>Bu talep kapatılmış. Mesaj gönderemezsiniz.</span>
                  </div>
      )}

      {/* Input */}
      <form className="chat-input-container" onSubmit={handleSendMessage}>
        {imagePreview && (
          <div className="image-preview-container">
            <img src={imagePreview} alt="Preview" className="image-preview" />
            <button 
              type="button" 
              className="remove-image-btn" 
              onClick={handleRemoveImage}
              disabled={uploadingImage}
            >
              <X size={16} />
            </button>
          </div>
        )}
        <div className="input-row">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            style={{ display: 'none' }}
            disabled={ticketStatus === 'closed'}
          />
          <button
            type="button"
            className="image-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={ticketStatus === 'closed' || uploadingImage}
            title="Resim ekle"
          >
            <Image size={20} />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={handleTyping}
            placeholder={ticketStatus === 'closed' ? 'Bu talep kapatılmış' : 'Mesajınızı yazın...'}
            className="chat-input"
            disabled={ticketStatus === 'closed' || uploadingImage}
            style={{
              opacity: ticketStatus === 'closed' ? 0.6 : 1,
              cursor: ticketStatus === 'closed' ? 'not-allowed' : 'text'
            }}
          />
          <button 
            type="submit" 
            className="send-btn" 
            disabled={(!newMessage.trim() && !selectedImage) || ticketStatus === 'closed' || uploadingImage}
            style={{
              opacity: ticketStatus === 'closed' || uploadingImage ? 0.5 : 1,
              cursor: ticketStatus === 'closed' || uploadingImage ? 'not-allowed' : 'pointer'
            }}
          >
            {uploadingImage ? '...' : <Send size={20} />}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TicketChat;